import { zodResolver } from '@hookform/resolvers/zod'
import { useGlobal } from '@jimmyboys/liita-ui/dist/components/GlobalContext'
import TaskHeaderErrors from 'components/TaskHeaderErrors/TaskHeaderErrors'
import { flatHookFormErrors } from 'controllers/TaskManager/controllers/utils'
import { Options } from 'models'
import { useEffect, useState } from 'react'
import { FormProvider, useForm, useWatch } from 'react-hook-form'
import { useFetchList } from 'store'
import styles from './../Dialog.module.sass'
import HFDropdown from 'components/HFInputs/HFDropdown'
import HFMultiSelect from 'components/HFInputs/HFMultipleSelect'
import ActionButton from '@jimmyboys/liita-ui/dist/components/ActionButton'
import VStack from '@jimmyboys/liita-ui/dist/components/VStack'
import HFTextInput from 'components/HFInputs/HFTextInput'
import { medicalActsNegotiationSchema } from './MedicalActsNegotiationSchema'
import { API_URL, fetchOptions, parseJSONContent } from 'store/fetchUtils'
import { translateErrorMessage } from 'hooks/useSubmitTask'
import useSession from 'hooks/useSession'

const MedicalActsNegotiationDialog = (props) => {
	const { i18n, pushNotification } = useGlobal()
	const { className, data, dismiss, resolve } = props
	const { logout } = useSession()
	const [submitting, setSubmitting] = useState(false)
	const [error, setError] = useState(null)

	const {
		contents: [providerEntities, insuranceCompanies, medicalActs],
	} = useFetchList(
		[
			'metadata/provider_entities',
			'metadata/insurance_companies',
			'metadata/medical_acts',
		],
		[
			Options.fromSimpleObjectJSON,
			Options.fromSimpleObjectJSON,
			Options.fromSimpleObjectJSON,
		]
	)

	const methods = useForm({
		mode: 'onSubmit',
		resolver: zodResolver(medicalActsNegotiationSchema),
		shouldUnregister: true,
	})

	const {
		handleSubmit,
		formState,
		setValue,
		watch,
		control,
		getValues,
		register,
	} = methods

	const onSubmit = (formData) => {
		if (submitting) return

		// console.log('formData', formData)

		formData.medicalActPrices.map((item) => {
			if ('price' in item) {
				if (isNaN(item.price) || item.price === undefined) {
					item.price = -0
					return
				}

				item.price = parseFloat(item.price)
				return
			}
		})

		const endpoint = `${API_URL}/api/clinical_processes/${data?.processId}/medical_act_negotiation`

		setError(null)
		setSubmitting('submitting')

		return fetch(
			endpoint,
			fetchOptions({
				method: 'POST',
				body: formData,
			})
		)
			.then((r) => {
				const res = parseJSONContent(r)

				// console.log('res', res)
				setSubmitting(false)
			})
			.catch((e) => {
				console.error('error ', e)
				if (e.status === '401' || e.status === 401) {
					logout()
				}

				const errorMessage = translateErrorMessage(e, i18n)

				setSubmitting(false)
				setError([errorMessage])
			})
	}

	useEffect(() => {
		const flattenErrors = flatHookFormErrors(formState.errors)

		if (flattenErrors) {
			const errorMessages = Object.keys(flattenErrors).map((errorKey) => {
				const errorMessage = flattenErrors[errorKey]
				const splittedErrorKey = errorKey.split('.')
				const field = splittedErrorKey[splittedErrorKey.length - 2]

				return `${i18n.translate(field)}: ${i18n.translate(
					errorMessage
				)}`
			})
			setError(errorMessages)
			// console.log('form ', getValues())
			// console.log(
			// 	'🚀 ~ file: AdministrativeOpinion.js ~ line 96 ~ useEffect ~ errorMessages',
			// 	errorMessages
			// )
		}

		// console.log(error)
		// console.log({ data })
	}, [formState.errors, i18n])

	const medicalActsWatch = useWatch({
		control,
		name: 'medicalActs',
		defaultValue: [],
	})

	return (
		<>
			<TaskHeaderErrors errors={error ?? []} />
			<FormProvider {...methods}>
				<form
					id={'form_medical_acts_negotiation'}
					onSubmit={handleSubmit(onSubmit)}
					className={`${styles['root'] || ''} ${className || ''}`}
					disabled={submitting}
				>
					<fieldset>
						<HFDropdown
							placeholder={i18n`establishment`}
							name='establishmentId'
							options={providerEntities ?? []}
							searchable={true}
							optionsAlign='right'
							noData={i18n`No results`}
							required
						/>
						<HFMultiSelect
							className={styles['value-selector']}
							placeholder={i18n`insuranceCompany`}
							name='providers'
							options={insuranceCompanies}
							searchable={true}
							optionsAlign='right'
							noData={i18n`No results`}
							required
						/>
						<HFMultiSelect
							className={styles['value-selector']}
							placeholder={i18n`Medical Act`}
							name='medicalActs'
							options={medicalActs ?? []}
							searchable={true}
							optionsAlign='right'
							noData={i18n`No results`}
							required
						/>
					</fieldset>

					{(medicalActsWatch || []).map((act, index) => {
						const medicalAct = medicalActs?.find(
							(ma) => ma.value === act
						)
						return (
							<fieldset key={act?.value ?? index}>
								<VStack gap='xsmall'>
									<label>
										{medicalAct?.text ??
											`Medical Act ${index + 1}`}
									</label>

									<input
										type='hidden'
										{...register(
											`medicalActPrices.${index}.medicalActId`
										)}
										value={act}
										readOnly
									/>
									<HFTextInput
										placeholder={i18n`Price optional`}
										name={`medicalActPrices.${index}.price`}
										type='number'
										// required
									/>
								</VStack>
							</fieldset>
						)
					})}
				</form>
			</FormProvider>
			<menu>
				<ActionButton type='button' secondary onClick={dismiss}>
					<small>
						<strong>{i18n`Cancel`}</strong>
					</small>
				</ActionButton>
				<ActionButton form={`form_medical_acts_negotiation`} primary>
					<small>
						<strong>
							{submitting ? i18n`Submitting` : i18n`Send`}
						</strong>
					</small>
				</ActionButton>
			</menu>
		</>
	)
}

export default MedicalActsNegotiationDialog
